import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AuthService from 'services/auth';
import { getPlayableGames } from 'constants/GameListAdapter';
import { APP_TYPES } from 'constants/AppTypes';
import RedeemCodeModal from '../RedeemCodeModal';
import placeHolderCard from 'images/gameStorePreviews/placeholder-card.svg';
import styles from '../ProfileStyles.module.scss';

const OwnedGames = () => {
    const { user } = useSelector(state => state.auth);
    const room = useSelector(state => state.room);
    const devices = useSelector(state => state.devices);
    const [userGames, setUserGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showRedeemModal, setShowRedeemModal] = useState(false);

    const isFireTvMode = room?.roomState?.platform === APP_TYPES.FIRE_TV || devices.limitAccessFireTv;

    useEffect(() => {
        const fetchGames = async () => {
            try {
                setLoading(true);
                const result = await AuthService.getGames(user?.email);
                if (result.games) {
                    setUserGames(result.games);
                } else if (result.Status === 'Error') {
                    setError(result.Message || 'Failed to load games');
                }
            } catch (err) {
                setError('Failed to load games. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (user?.email) {
            fetchGames();
        }
    }, [user?.email]);

    // Match user games to GameList entries and remove duplicates
    // Only show enabled individual games, not packs, addons, or disabled games
    const getOwnedGamesList = () => {
        const ownedGameIds = new Set();
        const ownedGameList = [];

        getPlayableGames().forEach((game) => {
            // Only include enabled games, not disabled ones
            if (game.status === 'disabled') return;

            // Check if user owns this game and hasn't been added yet
            if (userGames.findIndex((ug) => ug.code === game.gameId) > -1 && !ownedGameIds.has(game.gameId)) {
                ownedGameIds.add(game.gameId);
                ownedGameList.push(game);
            }
        });

        return ownedGameList;
    };

    if (loading) {
        return (
            <div>
                <h2 className={styles.sectionTitle}>My Games</h2>
                <div className={styles.loading}>
                    <div className={styles.loadingSpinner}></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <h2 className={styles.sectionTitle}>My Games</h2>
                <div className={styles.card}>
                    <p className={styles.errorText}>{error}</p>
                </div>
            </div>
        );
    }

    const ownedGames = getOwnedGamesList();

    const handleRedeemClose = (wasRedeemed) => {
        setShowRedeemModal(false);
        if (wasRedeemed && user?.email) {
            // Refresh games list after successful redemption
            const fetchGames = async () => {
                try {
                    const result = await AuthService.getGames(user.email);
                    if (result.games) {
                        setUserGames(result.games);
                    }
                } catch (err) {
                    // Silently fail - user can refresh manually
                }
            };
            fetchGames();
        }
    };

    return (
        <div>
            <h2 className={styles.sectionTitle}>My Games</h2>
            <p className={styles.sectionDescription}>
                All the games you own and can play.
            </p>
            {!isFireTvMode && (
                <Link to="/lobby" className={`${styles.button} ${styles.small}`}>
                    Host Game
                </Link>
            )}

            {ownedGames.length === 0 ? (
                <div className={styles.card}>
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>🎮</div>
                        <h3 className={styles.emptyTitle}>No Games Yet</h3>
                        <p className={styles.emptyText}>
                            You don't have any games in your account yet.
                        </p>
                        <p className={styles.emptyText}>
                            If you have an activation code, click below to add games to your account.
                            Already redeemed codes? Make sure you're logged into the correct account.
                        </p>
                        <button
                            onClick={() => setShowRedeemModal(true)}
                            className={`${styles.button} ${styles.small}`}
                        >
                            Redeem Code
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <p className={styles.sectionDescription}>
                        {ownedGames.length} game{ownedGames.length !== 1 ? 's' : ''} in your library
                    </p>
                    <div className={styles.ownedGamesGrid}>
                        {ownedGames.map((game) => (
                            game.meta?.tinyCard ? (
                                <img
                                    key={game.gameId}
                                    src={game.meta.tinyCard}
                                    alt={game.name}
                                    className={styles.ownedGameImage}
                                />
                            ) : (
                                <div key={game.gameId} className={styles.ownedGamePlaceholder}>
                                    <span className={styles.placeholderName}>{game.name}</span>
                                    <img src={placeHolderCard} alt={game.name} className={styles.ownedGameImage} />
                                </div>
                            )
                        ))}
                    </div>
                </>
            )}

            {showRedeemModal && (
                <RedeemCodeModal onClose={handleRedeemClose} />
            )}
        </div>
    );
};

export default OwnedGames;
